import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';
import { useLoading } from '../context/LoadingContext';

const AnimatedHero = React.memo(() => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationIdRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const { isInitialized } = useLoading();
  
  // Memoize configuration values
  const config = useMemo(() => ({
    particleCount: 250, // Reduced from 1000
    targetFPS: 30, // Limit frame rate
    mouseInfluence: 0.00005, // Reduced mouse sensitivity
    showShield: false // Hide the central blue circle for a cleaner hero
  }), []);

  // Optimized mouse move handler with throttling
  const handleMouseMove = useCallback((event) => {
    mousePositionRef.current = {
      x: event.clientX,
      y: event.clientY
    };
  }, []);

  // Optimized resize handler
  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.camera || !canvas.renderer) return;

    canvas.camera.aspect = window.innerWidth / window.innerHeight;
    canvas.camera.updateProjectionMatrix();
    canvas.renderer.setSize(window.innerWidth, window.innerHeight);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    
    let scene, camera, renderer, shield, particles;
    
    const initThreeJS = () => {
      try {
        if (!canvasRef.current) return;

        // Three.js setup with performance optimizations
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        renderer = new THREE.WebGLRenderer({ 
          canvas: canvasRef.current,
          alpha: true,
          antialias: false, // Disabled for performance
          powerPreference: "high-performance",
          stencil: false,
          depth: true
        });
        
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio
        camera.position.z = 5;

        // Store references for resize handler
        canvasRef.current.camera = camera;
        canvasRef.current.renderer = renderer;

        // Optionally create a decorative shield — currently disabled to remove the blue circle
        if (config.showShield) {
          const shieldGeometry = new THREE.CircleGeometry(1, 16); // Reduced from 32
          const shieldMaterial = new THREE.MeshBasicMaterial({ // Changed from MeshPhongMaterial for performance
            color: 0x3b82f6,
            transparent: true,
            opacity: 0.8,
            side: THREE.DoubleSide
          });
          shield = new THREE.Mesh(shieldGeometry, shieldMaterial);
          scene.add(shield);
        }

        // Simplified lighting (remove heavy point light)
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);

        // Create optimized particles with reduced count
        const particleGeometry = new THREE.BufferGeometry();
        const positions = new Float32Array(config.particleCount * 3);
        
        for(let i = 0; i < config.particleCount * 3; i += 3) {
          positions[i] = (Math.random() - 0.5) * 10;
          positions[i + 1] = (Math.random() - 0.5) * 10;
          positions[i + 2] = (Math.random() - 0.5) * 10;
        }
        
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        const particleMaterial = new THREE.PointsMaterial({
          color: 0x93c5fd,
          size: 0.03, // Slightly larger to compensate for fewer particles
          transparent: true,
          opacity: 0.8,
          sizeAttenuation: false // Performance optimization
        });
        
        particles = new THREE.Points(particleGeometry, particleMaterial);
        scene.add(particles);

        // Frame-rate limited animation loop
        const animate = (currentTime) => {
          animationIdRef.current = requestAnimationFrame(animate);
          
          // Limit frame rate
          if (currentTime - lastFrameTimeRef.current < (1000 / config.targetFPS)) {
            return;
          }
          lastFrameTimeRef.current = currentTime;
          
          if (shield) {
            shield.rotation.y += 0.003; // Reduced rotation speed
          }
          if (particles) {
            particles.rotation.y += 0.0005; // Reduced rotation speed
          }
          
          // Optimized mouse interaction
          const mouseX = mousePositionRef.current.x - window.innerWidth / 2;
          const mouseY = mousePositionRef.current.y - window.innerHeight / 2;
          if (shield) {
            shield.rotation.x += (mouseY * config.mouseInfluence - shield.rotation.x) * 0.05;
            shield.rotation.y += (mouseX * config.mouseInfluence - shield.rotation.y) * 0.05;
          }
          
          renderer.render(scene, camera);
        };

        // Event listeners with passive option for performance
        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('resize', handleResize, { passive: true });
        
        // Start animation
        animate(0);

        // Optimized GSAP animations
        if (shield) {
          gsap.fromTo(shield.scale, 
            { x: 0, y: 0, z: 0 },
            { 
              x: 1, y: 1, z: 1,
              duration: 1.5,
              ease: "elastic.out(1, 0.3)"
            }
          );

          gsap.fromTo(shield.rotation, 
            { y: Math.PI * 2 },
            {
              y: 0,
              duration: 2,
              ease: "power2.out"
            }
          );
        }

      } catch (error) {
        console.error('Error initializing Three.js:', error);
      }
    };

    if (isInitialized) {
      // Use requestIdleCallback for non-critical initialization
      if (window.requestIdleCallback) {
        window.requestIdleCallback(initThreeJS);
      } else {
        setTimeout(initThreeJS, 0);
      }
    }

    // Cleanup function
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);

      // Proper Three.js cleanup
      if (scene) {
        scene.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        scene.clear();
      }
      
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
      }

      // Clear stored references
      if (canvasRef.current) {
        canvasRef.current.camera = null;
        canvasRef.current.renderer = null;
      }
    };
  }, [isInitialized, config, handleMouseMove, handleResize]);

  return (
    <div ref={containerRef} className="absolute inset-0 z-0 pointer-events-none">
      <canvas 
        ref={canvasRef} 
        className="w-full h-full"
        style={{ willChange: 'transform' }} // GPU acceleration hint
      />
    </div>
  );
});

AnimatedHero.displayName = 'AnimatedHero';

export default AnimatedHero; 